import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    description: "How YourPlants.in collects, uses, and protects your personal information when you shop, book services, or use AI Plant Doctor and Plant Scan.",
    sections: [
      {
        heading: "Information we collect",
        body: "We collect the information you give us when you create an account, place an order, book a nursery visit, or contact support — such as your name, phone number, email, delivery address, and pincode. We also collect basic usage data (pages viewed, searches made) to improve our catalogue and recommendations.",
      },
      {
        heading: "How we use your information",
        body: "Your information is used to process orders, coordinate delivery and Pay After Check verification, send order and service updates, respond to support requests, and improve the YourPlants.in experience. Photos you upload to Plant Scan or AI Plant Doctor are used only to generate your diagnosis or recommendation.",
      },
      {
        heading: "Sharing of information",
        body: "We share your information only with the nurseries, delivery partners, and payment providers needed to fulfil your order. We do not sell your personal data to third parties.",
      },
      {
        heading: "Data security",
        body: "We use reasonable technical and organisational measures to protect your data. You can request access to, correction of, or deletion of your data at any time by writing to us.",
      },
      {
        heading: "Contact",
        body: "For any privacy questions, email support@yourplants.in or call +91 8011441118.",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    description: "The terms governing orders, Pay After Check, pricing, and use of YourPlants.in's marketplace and plant-care tools.",
    sections: [
      {
        heading: "Using YourPlants.in",
        body: "By browsing or ordering from YourPlants.in you agree to these terms. YourPlants.in is a marketplace connecting you with trusted local nurseries; product descriptions, sizes, and photos are indicative — live plants naturally vary in shape, height, and foliage.",
      },
      {
        heading: "Orders and Pay After Check",
        body: "With Pay After Check, you inspect your plant at the doorstep before completing payment for eligible orders and pincodes. Once you've accepted the plant and paid, the order is considered fulfilled and is not eligible for return.",
      },
      {
        heading: "AI Plant Doctor, Plant Scan & Design My Space",
        body: "These tools provide general guidance based on the photos and information you share and are not a substitute for professional horticultural or landscaping advice. Recommendations may vary based on your local climate and conditions.",
      },
      {
        heading: "Pricing and availability",
        body: "Prices, offers, and plant availability can change without notice and may vary by nursery and location. We reserve the right to cancel an order if an item becomes unavailable after purchase, with a full refund of the amount paid.",
      },
      {
        heading: "Governing law",
        body: "These terms are governed by the laws of India. Any disputes will be subject to the jurisdiction of the courts where YourPlants.in operates.",
      },
    ],
  },
  shipping: {
    title: "Shipping Policy",
    description: "Delivery areas, timelines, and Pay After Check delivery details for orders placed on YourPlants.in.",
    sections: [
      {
        heading: "Delivery areas",
        body: "We currently deliver across Assam, Meghalaya, Arunachal Pradesh, and Delhi, with select nurseries also serving nearby pincodes. Enter your pincode on any product page to check delivery availability.",
      },
      {
        heading: "Delivery timelines",
        body: "Standard delivery takes 3–7 days depending on your pincode and the sourcing nursery. Bulk orders and nursery visit bookings follow separate schedules confirmed at the time of enquiry.",
      },
      {
        heading: "Pay After Check delivery",
        body: "For eligible orders, our delivery partner brings the plant to your doorstep for inspection. Payment is collected only after you've checked and accepted the plant.",
      },
      {
        heading: "Shipping charges",
        body: "Shipping charges, where applicable, are shown at checkout before you confirm your order.",
      },
    ],
  },
  refund: {
    title: "Refund Policy",
    description: "When YourPlants.in issues a refund — Pay After Check declines, cancelled orders, and damaged-on-arrival prepaid orders.",
    sections: [
      {
        heading: "No returns on accepted plants",
        body: "Because we sell live plants, we do not accept returns once a plant has been inspected and accepted through Pay After Check, or once a prepaid order has been delivered and accepted.",
      },
      {
        heading: "When a refund applies",
        body: "A refund is issued if: (a) you decline a plant at the doorstep during Pay After Check before paying, (b) an order is cancelled by YourPlants.in due to unavailability, or (c) a prepaid order arrives visibly damaged or dead-on-arrival and you share photos with our support team within 24 hours of delivery.",
      },
      {
        heading: "Refund timelines",
        body: "Approved refunds are processed to your original payment method within 5–7 business days.",
      },
      {
        heading: "How to request a refund",
        body: "Email support@yourplants.in or call +91 8011441118 with your order ID and, if applicable, photos of the plant as received.",
      },
    ],
  },
};

export default function Policy() {
  const { slug } = useParams();
  const p = POLICIES[slug];

  if (!p) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-14">
        <Seo title="Not Found" path={`/policies/${slug}`} noindex />
        <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">
          <Link to="/">Home</Link> · Policies
        </div>
        <h1 className="font-serif-display text-4xl text-[var(--yp-primary)] mt-2">Not found</h1>
        <p className="mt-6 leading-relaxed text-[var(--yp-text)]">This page doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-14">
      <Seo title={p.title} description={p.description} path={`/policies/${slug}`} />
      <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">
        <Link to="/">Home</Link> · Policies
      </div>
      <h1 className="font-serif-display text-4xl text-[var(--yp-primary)] mt-2">{p.title}</h1>
      <div className="mt-8 space-y-8">
        {p.sections.map((s) => (
          <div key={s.heading}>
            <h2 className="text-lg font-semibold text-[var(--yp-primary)]">{s.heading}</h2>
            <p className="mt-2 leading-relaxed text-[var(--yp-text)]">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
